import React from 'react';
import { createBrowserRouter, Navigate } from 'react-router';

import { ErrorBoundary } from '../components/ErrorBoundary';
import AuthLayout from '../layout/Auth/AuthLayout';
import DashboardLayout from '../layout/Dashboard/DashboardLayout';
import ChangePassword from '../pages/Auth/ChangePassword/ChangePassword';
import ChangePasswordVerificationConfirmation from '../pages/Auth/ChangePasswordVerificationConfirmation/ChangePasswordVerificationConfirmation';
import ForgotPassword from '../pages/Auth/ForgotPassword/ForgotPassword';
import ForgotPasswordNotification from '../pages/Auth/ForgotPasswordNotification/ForgotPasswordNotification';
import SignIn from '../pages/Auth/SignIn/SignIn';
import BADriverVerificationSalesAdmin from '../pages/Dashboard/BADriverVerificationSalesAdmin/BADriverVerificationSalesAdmin';
import BADriverVerificationSuperAdmin from '../pages/Dashboard/BADriverVerificationSuperAdmin/BADriverVerificationSuperAdmin';
import BADriverVerificationViewComplaintSalesAdmin from '../pages/Dashboard/BADriverVerificationViewComplaintSalesAdmin/BADriverVerificationViewComplaintSalesAdmin';
import CompanyInsight from '../pages/Dashboard/CompanyInsight/CompanyInsight';
import CompanyInsightSearchResult from '../pages/Dashboard/CompanyInsightSearchResult/CompanyInsightSearchResult';
import Dashboard from '../pages/Dashboard/Dashboard/Dashboard';
import DemoRequests from '../pages/Dashboard/DemoRequests/DemoRequests';
import DriverHistoryConflictDetails from '../pages/Dashboard/DriverHistoryConflictDetails/DriverHistoryConflictDetails';
import DriverHistoryDriverList from '../pages/Dashboard/DriverHistoryDriverList/DriverHistoryDriverList';
import DriverHistoryPastCompanies from '../pages/Dashboard/DriverHistoryPastCompanies/DriverHistoryPastCompanies';
import DriverInformation from '../pages/Dashboard/DriverInformation/DriverInformation';
import DriverPerformanceInformation from '../pages/Dashboard/DriverPerformanceInformation/DriverPerformanceInformation';
import KycCompliance from '../pages/Dashboard/KycCompliance/KycCompliance';
import Notification from '../pages/Dashboard/Notification/Notification';
import PerformanceMonitoring from '../pages/Dashboard/PerformanceMonitoring/PerformanceMonitoring';
import PerformanceMonitoringSearchResults from '../pages/Dashboard/PerformanceMonitoringSearchResults/PerformanceMonitoringSearchResults';
import PerformanceMonitoringSearchResultsConflictDetails from '../pages/Dashboard/PerformanceMonitoringSearchResultsConflictDetails/PerformanceMonitoringSearchResultsConflictDetails';
import PerformanceMonitoringViewPerformance from '../pages/Dashboard/PerformanceMonitoringViewPerformance/PerformanceMonitoringViewPerformance';
import Revenue from '../pages/Dashboard/Revenue/Revenue';
import RevenuePriceManagement from '../pages/Dashboard/RevenuePriceManagement/RevenuePriceManagement';
import UserManagement from '../pages/Dashboard/UserManagement/UserManagement';
import UserManagementBusinessProfile from '../pages/Dashboard/UserManagementBusinessProfile/UserManagementBusinessProfile';
import UserManagementOverallDriverPerformance from '../pages/Dashboard/UserManagementOverallDriverPerformance/UserManagementOverallDriverPerformance';
import WaitlistSubmissions from '../pages/Dashboard/WaitlistSubmissions/WaitlistSubmissions';
import { NotFound404 } from '../pages/NotFound404';
import {
  ACCOUNT,
  BA_DRIVER_VERIFICATION,
  BA_DRIVER_VERIFICATION_SUPER_ADMIN,
  BA_DRIVER_VERIFICATION_VIEW_COMPLAINT_SALES_ADMIN,
  BUSINESS_PROFILE,
  CHANGE_PASSWORD,
  CHANGE_PASSWORD_VERIFICATION_CONFIRMATION,
  COMPANY_INSIGHTS,
  COMPANY_INSIGHTS_DRIVER_LIST,
  COMPANY_INSIGHTS_DRIVER_LIST_PAST_COMPANIES,
  COMPANY_INSIGHTS_DRIVER_LIST_PAST_COMPANIES_CONFLICT_DETAILS,
  COMPANY_INSIGHTS_SEARCH_RESULTS,
  DASHBOARD,
  DEMO_REQUESTS,
  DRIVER_INFORMATION,
  DRIVER_OVERALL_PERFORMANCE,
  FORGOT_PASSWORD,
  FORGOT_PASSWORD_NOTIFICATION,
  KYC_COMPLIANCE,
  NOTFOUND,
  NOTIFICATIONS,
  PERFORMANCE_MONITORING,
  PERFORMANCE_MONITORING_SEARCH_RESULTS,
  PERFORMANCE_MONITORING_SEARCH_RESULTS_CONFLICT_DETAILS,
  PERFORMANCE_MONITORING_VIEW_PERFORMANCE,
  REVENUE,
  REVENUE_PRICE_MANAGEMENT,
  SIGNIN,
  USER_MANAGEMENT,
  WAITLIST_SUBMISSIONS,
} from './routeConstants';

export const router = createBrowserRouter([
  {
    path: '/',
    element: <DashboardLayout />,
    errorElement: <ErrorBoundary />,
    children: [
      { path: '/', element: <Navigate to={DASHBOARD} /> },
      { path: DASHBOARD, element: <Dashboard /> },
      { path: KYC_COMPLIANCE, element: <KycCompliance /> },
      { path: USER_MANAGEMENT, element: <UserManagement /> },
      {
        path: `${BUSINESS_PROFILE}/:jsonData`,
        element: <UserManagementBusinessProfile />,
      },
      {
        path: `${DRIVER_INFORMATION}/:companyId/:companyName`,
        element: <DriverInformation />,
      },
      {
        path: `${DRIVER_INFORMATION}/:companyId/:companyName/:driverId/performance/:jsonData`,
        element: <DriverPerformanceInformation />,
      },
      {
        path: `${DRIVER_INFORMATION}/:companyId/:companyName/:driverId/performance/:jsonData/${DRIVER_OVERALL_PERFORMANCE}`,
        element: <UserManagementOverallDriverPerformance />,
      },
      { path: PERFORMANCE_MONITORING, element: <PerformanceMonitoring /> },
      {
        path: PERFORMANCE_MONITORING_VIEW_PERFORMANCE,
        element: <PerformanceMonitoringViewPerformance />,
      },

      {
        path: `${PERFORMANCE_MONITORING_SEARCH_RESULTS}/:nin`,
        element: <PerformanceMonitoringSearchResults />,
      },

      {
        path: PERFORMANCE_MONITORING_SEARCH_RESULTS_CONFLICT_DETAILS,
        element: <PerformanceMonitoringSearchResultsConflictDetails />,
      },

      { path: REVENUE, element: <Revenue /> },
      { path: REVENUE_PRICE_MANAGEMENT, element: <RevenuePriceManagement /> },

      { path: COMPANY_INSIGHTS, element: <CompanyInsight /> },
      {
        path: `${COMPANY_INSIGHTS_SEARCH_RESULTS}/:companyId/:companyName`,
        element: <CompanyInsightSearchResult />,
      },
      {
        path: COMPANY_INSIGHTS_DRIVER_LIST,
        element: <DriverHistoryDriverList />,
      },
      {
        path: COMPANY_INSIGHTS_DRIVER_LIST_PAST_COMPANIES,
        element: <DriverHistoryPastCompanies />,
      },
      {
        path: COMPANY_INSIGHTS_DRIVER_LIST_PAST_COMPANIES_CONFLICT_DETAILS,
        element: <DriverHistoryConflictDetails />,
      },
      { path: NOTIFICATIONS, element: <Notification /> },
      { path: NOTFOUND, element: <NotFound404 /> },

      // ----End----

      // Admin-specific routes
      {
        path: `${WAITLIST_SUBMISSIONS}`,
        element: <WaitlistSubmissions />,
      },
      {
        path: `${DEMO_REQUESTS}`,
        element: <DemoRequests />,
      },

      {
        path: `${BA_DRIVER_VERIFICATION}`,
        element: <BADriverVerificationSalesAdmin />,
      },

      {
        path: `${BA_DRIVER_VERIFICATION_VIEW_COMPLAINT_SALES_ADMIN}`,
        element: <BADriverVerificationViewComplaintSalesAdmin />,
      },

      // {
      //   path: `${BA_DRIVER_VERIFICATION}`,
      //   element: <BADriverVerification />,
      // },

      {
        path: `${BA_DRIVER_VERIFICATION_SUPER_ADMIN}`,
        element: <BADriverVerificationSuperAdmin />,
      },

      // ----

      { path: '*', element: <Navigate to={`/${NOTFOUND}`} replace /> },
    ],
  },

  {
    path: ACCOUNT,
    element: <AuthLayout />,
    errorElement: <ErrorBoundary />,
    children: [
      { path: SIGNIN, element: <SignIn /> },
      { path: FORGOT_PASSWORD, element: <ForgotPassword /> },
      {
        path: FORGOT_PASSWORD_NOTIFICATION,
        element: <ForgotPasswordNotification />,
      },
      { path: CHANGE_PASSWORD, element: <ChangePassword /> },
      {
        path: CHANGE_PASSWORD_VERIFICATION_CONFIRMATION,
        element: <ChangePasswordVerificationConfirmation />,
      },
      { path: NOTFOUND, element: <NotFound404 /> },
      { path: '*', element: <Navigate to={`/${NOTFOUND}`} replace /> },
    ],
  },

  { path: '*', element: <NotFound404 />, errorElement: <ErrorBoundary /> },
  // {
  //   path: BA_TRACKING,
  //   element: <BATracking />,
  //   errorElement: <ErrorBoundary />,
  // },
]);
